import { InfoIcon } from '@mm/icons';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import {
  IceBreakerKind,
  IceBreakerModel,
} from '@mm/state/model/IceBreakerModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectInstagramShareLinks } from '@mm/state/selectors/promoterSelector';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { some } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Switch from '../../../../common/components/Switch/Switch';
import {
  ActivePathSelection,
  ActivePathType,
} from '../../../../common/widgets/common/ActivePathSelection/ActivePathSelection';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { AddButton } from './AddButton';
import { ICE_BREAKER_BUTTONS_LIMIT } from './constants';
import './IceBreaker.pcss';
import { IceBreakerButtonsStack } from './IceBreakerButtonsStack/IceBreakerButtonsStack';
import { IceBreakerCreateButton } from './IceBreakerCreateButton/IceBreakerCreateButton';

type Props = {
  platform?: IceBreakerKind;
  customInfo?: string;
  iceBreaker?: IceBreakerModel;
  showActivation?: boolean;
  showHeading?: boolean;
  widgetsToExclude?: WidgetTypes[];
  platforms?: PlatformType[];
  usedInSlideout?: boolean;
  className?: string;
};

export const IceBreaker = ({
  platform = 'facebook',
  customInfo,
  iceBreaker,
  showActivation = true,
  showHeading = true,
  widgetsToExclude = [],
  usedInSlideout,
  platforms,
  className,
}: Props) => {
  const pathGraph = useNewGraphInstance(undefined);
  return (
    <PathGraphContext.Provider value={pathGraph}>
      <IceBreakerInner
        className={className}
        platform={platform}
        customInfo={customInfo}
        iceBreaker={iceBreaker}
        showActivation={showActivation}
        showHeading={showHeading}
        widgetsToExclude={widgetsToExclude}
        usedInSlideout={usedInSlideout}
        platforms={platforms}
      />
    </PathGraphContext.Provider>
  );
};

const IceBreakerInner = ({
  platform,
  customInfo,
  iceBreaker,
  showActivation,
  showHeading,
  widgetsToExclude,
  usedInSlideout,
  platforms,
  className,
}: Props) => {
  const { getActiveButtonFromWidgetButtons, deselectWidgetButtons } =
    useContext(PathGraphContext);

  const pages = useSelector((state: StoreState) => state.page.entities);
  const activeIGShareLink = useSelector(selectInstagramShareLinks)?.find(
    link => link.active,
  );
  const pageCRUDActions = usePageCRUDActions();
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();

  useEffect(() => {
    if (iceBreaker) {
      if (
        some(
          iceBreaker.buttons.find(
            ({ config: { pageId } }) =>
              pages.findIndex(({ id }) => pageId === id) === -1,
          ),
        )
      ) {
        pageCRUDActions.retrieveAll(iceBreaker.botId);
      }
    }
  }, [iceBreaker]);

  const [openedButtonId, setOpenedButtonId] = useState<number | undefined>();
  const [showCreateButton, setShowCreateButton] = useState(false);
  const [activePathType, setActivePathType] =
    useState<ActivePathType>('regular');
  const [activePageId, setActivePageId] = useState<number | undefined>();
  const onClickAddNew = async () => {
    if (!iceBreaker) {
      return;
    }
    deselectWidgetButtons?.(iceBreaker.buttons);
    setOpenedButtonId(undefined);
    setShowCreateButton(true);
  };

  if (!iceBreaker) {
    return null;
  }

  const activeButton = getActiveButtonFromWidgetButtons?.(iceBreaker.buttons);

  const onToggleActivationLabel = () => {
    if (activeIGShareLink) {
      openGeneralConfirmModal({
        headerText: 'Deactivate All Instagram Share Links',
        messageText:
          'In order to deactivate Instagram Icebreaker you must deactivate all Instagram share links first',
        onCloseHandler: ok => {},
        okButtonText: 'Close',
        okButtonStyling: { blue: true },
        showCancelButton: false,
      });
      return;
    }
    iceBreakerCRUDActions.update(iceBreaker.botId, {
      id: iceBreaker.id,
      active: !iceBreaker.active,
    });
  };

  return (
    <div className="ice-breaker__container">
      {showHeading && (
        <div className="ice-breaker__heading-container">
          <div className="ice-breaker__heading-left">
            <div
              className={classNames('ice-breaker__info-message', {
                'ice-breaker__info-message--slideout': usedInSlideout,
              })}
            >
              <InfoIcon className="ice-breaker__info-icon" />
              <span>
                {customInfo ||
                  'Add some frequently asked questions to help customers start a conversation with your FB page.'}
              </span>
            </div>
          </div>
          {showActivation && (
            <div className="ice-breaker__heading-right">
              <Switch
                checked={iceBreaker.active}
                disabled={false}
                onChange={onToggleActivationLabel}
              />
            </div>
          )}
        </div>
      )}
      <div
        className={classNames('ice-breaker__buttons-area', {
          'ice-breaker__buttons-area--active': activeButton || showCreateButton,
        })}
      >
        <div className="ice-breaker__buttons">
          <IceBreakerButtonsStack
            setActivePageId={setActivePageId}
            iceBreaker={iceBreaker}
            openedButtonId={openedButtonId}
            setOpenedButtonId={(id: number) => {
              setOpenedButtonId(id);
              setShowCreateButton(false);
            }}
            setActivePathType={setActivePathType}
          />
          {showCreateButton && (
            <IceBreakerCreateButton
              iceBreaker={iceBreaker}
              closeButtonArea={() => setShowCreateButton(false)}
              onCreate={button => {
                setOpenedButtonId(button.id);
                setActivePageId(button.config.pageId);
              }}
            />
          )}
          <AddButton
            className="ice-breaker__add-button"
            onClick={onClickAddNew}
            disabled={iceBreaker.buttons.length >= ICE_BREAKER_BUTTONS_LIMIT}
          />
        </div>
        {activeButton && (
          <ActivePathSelection
            className={className}
            chatbotId={iceBreaker.botId}
            iceBreaker={iceBreaker}
            dialogueId={activePageId}
            activeButton={activeButton}
            widgetsToExclude={widgetsToExclude}
            forInstagram={platform === 'instagram'}
            platforms={platforms}
          />
        )}
      </div>
    </div>
  );
};
